import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, DatePicker, Input, InputNumber, message, Modal, Select, Space, Spin, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useAuth } from '../../../components/common/security/AuthContext';
import WPermitSelect from '../../../components/modals/WPermitSelect';
import MachineSelect from '../../../components/modals/MachineSelect';
import PersonnelSelect from '../../../components/modals/PersonnelSelect';
import CompaniesSelect from '../../../components/modals/CompaniesSelect';
import { saveP6ProgressUpdate } from '../../../components/common/P6ProgressUpdate';
import { fetchSqlDataWithQuery, insertData } from '../../../services/api';
import { useGetPersonnel, useGetPrimaveraTaskSmallByCode, useGetTkfTaskResources } from '../../../hooks/genericHooks';
import type { PrimaveraTaskSmall, VwTkfTaskResources } from '../../../types';
import Button from '../../../components/common/Button';

const { Title, Text } = Typography;

type ProviderRow = {
  key: string;
  provider_code: string;
  provider_name: string;
  reported_qty: number | null;
  remarks: string;
};

type LaborProviderOption = {
  provider_code: string;
  provider_name: string;
};

const toNum = (v: any): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const clampNonNegative = (v: number): number => {
  return v < 0 ? 0 : v;
};

const unwrapRows = <T,>(resp: any): T[] => {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp as T[];
  if (Array.isArray(resp?.data)) return resp.data as T[];
  if (Array.isArray(resp?.rows)) return resp.rows as T[];
  if (Array.isArray(resp?.recordset)) return resp.recordset as T[];
  return [];
};

const parsePersonnelList = (v: any): string[] => {
  if (!v) return [];
  if (Array.isArray(v)) return v.map(x => String(x)).filter(Boolean);
  if (typeof v === 'string') {
    const s = v.trim();
    if (!s) return [];
    // Support Postgres text[] like {1,2} and CSV like 1,2
    const cleaned = s.startsWith('{') && s.endsWith('}') ? s.slice(1, -1) : s;
    return cleaned
      .split(',')
      .map(x => x.replace(/^"|"$/g, '').trim())
      .filter(Boolean);
  }
  return [];
};

const ToPlnActualsReport: React.FC = () => {
  const { user } = useAuth();

  const { data: personnelResp } = useGetPersonnel();
  const personnelRows: any[] = useMemo(() => {
    if (Array.isArray((personnelResp as any)?.data)) return (personnelResp as any).data as any[];
    if (Array.isArray(personnelResp)) return personnelResp as any[];
    return [];
  }, [personnelResp]);

  const [progressDate, setProgressDate] = useState<Dayjs>(() => dayjs());

  const [wpModalOpen, setWpModalOpen] = useState(false);
  const [wpRefNum, setWpRefNum] = useState<string>('');
  const [wpDesc, setWpDesc] = useState<string>('');
  const [taskCode, setTaskCode] = useState<string>('');
  const [taskNameFromWp, setTaskNameFromWp] = useState<string>('');

  const [wpPersonnelIds, setWpPersonnelIds] = useState<string[]>([]);

  useEffect(() => {
    let mounted = true;
    if (!wpRefNum || String(wpRefNum).trim() === '') {
      setWpPersonnelIds([]);
      return;
    }

    (async () => {
      try {
        const ref = String(wpRefNum).replace(/'/g, "''");
        const sql = `
          SELECT personnel_list
          FROM public.hse_workpermits
          WHERE ref_num = '${ref}'
          ORDER BY id DESC
          LIMIT 1;
        `;
        const resp = await fetchSqlDataWithQuery(sql);
        const rows = unwrapRows<{ personnel_list: any }>(resp);
        const ids = rows.length ? parsePersonnelList((rows[0] as any).personnel_list) : [];
        if (!mounted) return;
        setWpPersonnelIds(ids);
      } catch {
        if (!mounted) return;
        setWpPersonnelIds([]);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [wpRefNum]);

  const { data: taskResp, isLoading: isLoadingTask } = useGetPrimaveraTaskSmallByCode(taskCode || null);
  const task: PrimaveraTaskSmall | null = useMemo(() => {
    const rows = unwrapRows<PrimaveraTaskSmall>(taskResp);
    if (!rows.length) return null;
    return rows[0];
  }, [taskResp]);

  const taskId = task?.task_id != null ? Number(task.task_id) : null;

  const { data: taskRsrcResp, isLoading: isLoadingTaskRsrc } = useGetTkfTaskResources(taskId);
  const taskResources: VwTkfTaskResources[] = useMemo(() => unwrapRows<VwTkfTaskResources>(taskRsrcResp), [taskRsrcResp]);

  const resourceOptions = useMemo(() => {
    const uniq = new Map<number, { label: string; value: number }>();
    (taskResources || []).forEach((r: any) => {
      const id = r?.taskrsrc_id != null ? Number(r.taskrsrc_id) : NaN;
      if (!Number.isFinite(id)) return;
      if (uniq.has(id)) return;
      const shortName = String((r as any)?.rsrc_short_name ?? '').trim();
      const name = String((r as any)?.rsrc_name ?? '').trim();
      const rsrcType = String((r as any)?.rsrc_type ?? '').trim();
      const unitName = String((r as any)?.unit_name ?? '').trim();
      const labelBase = name || shortName || `TASKRSRC ${id}`;
      const shortHint = shortName && shortName !== labelBase ? `[${shortName}]` : '';
      const typeHint = rsrcType ? `{${rsrcType}}` : '';
      const labelParts = [labelBase, shortHint, typeHint, unitName ? `(${unitName})` : ''].filter(Boolean);
      uniq.set(id, { value: id, label: labelParts.join(' ') });
    });
    return Array.from(uniq.values());
  }, [taskResources]);

  const [selectedTaskRsrcId, setSelectedTaskRsrcId] = useState<number | null>(null);

  const selectedTaskRsrcRow: VwTkfTaskResources | null = useMemo(() => {
    if (selectedTaskRsrcId == null) return null;
    const found = (taskResources || []).find((r: any) => String((r as any)?.taskrsrc_id) === String(selectedTaskRsrcId));
    return found ?? null;
  }, [selectedTaskRsrcId, taskResources]);

  const selectedRsrcType = selectedTaskRsrcRow?.rsrc_type != null ? String((selectedTaskRsrcRow as any).rsrc_type) : null;
  const isLabor = selectedRsrcType === 'RT_Labor';
  const isEquip = selectedRsrcType === 'RT_Equip';
  const isMat = selectedRsrcType === 'RT_Mat';

  useEffect(() => {
    setSelectedTaskRsrcId(null);
  }, [taskId]);

  const [providerRows, setProviderRows] = useState<ProviderRow[]>(() => [
    {
      key: 'row-1',
      provider_code: '',
      provider_name: '',
      reported_qty: null,
      remarks: '',
    },
  ]);

  useEffect(() => {
    setProviderRows([
      {
        key: 'row-1',
        provider_code: '',
        provider_name: '',
        reported_qty: null,
        remarks: '',
      },
    ]);
  }, [taskCode, selectedTaskRsrcId, wpRefNum]);

  const [laborProviderOptions, setLaborProviderOptions] = useState<LaborProviderOption[]>([]);

  useEffect(() => {
    if (!isLabor) return;
    const opts: LaborProviderOption[] = (wpPersonnelIds || [])
      .map((pid) => {
        const rec = (personnelRows || []).find((p: any) => String(p?.pers_id) === String(pid));
        const fullName = rec ? `${rec.first_name || ''} ${rec.last_name || ''}`.trim() : '';
        return {
          provider_code: String(pid),
          provider_name: fullName || String(pid),
        };
      })
      .filter((x) => x.provider_code && x.provider_code.trim() !== '');
    setLaborProviderOptions(opts);
  }, [isLabor, personnelRows, wpPersonnelIds]);

  useEffect(() => {
    if (!isLabor) return;
    if (!selectedTaskRsrcId) return;

    setProviderRows((prev) => {
      const prevRows = Array.isArray(prev) ? prev : [];
      const prevByCode = new Map<string, ProviderRow>();
      prevRows.forEach((r) => {
        const code = String(r.provider_code ?? '').trim();
        if (!code) return;
        prevByCode.set(code, r);
      });

      const wpRows: ProviderRow[] = (laborProviderOptions || []).map((p) => {
        const code = String(p.provider_code ?? '').trim();
        const existing = code ? prevByCode.get(code) : undefined;
        return {
          key: existing?.key ?? `pers-${code}`,
          provider_code: code,
          provider_name: String(p.provider_name ?? '').trim() || code,
          reported_qty: existing?.reported_qty ?? null,
          remarks: existing?.remarks ?? '',
        };
      });

      const wpCodes = new Set(wpRows.map((r) => String(r.provider_code ?? '').trim()).filter(Boolean));
      const extras = prevRows.filter((r) => {
        const code = String(r.provider_code ?? '').trim();
        return code && !wpCodes.has(code);
      });

      return [...wpRows, ...extras];
    });
  }, [isLabor, laborProviderOptions, selectedTaskRsrcId]);

  const taskStatus = task?.status_code != null ? String((task as any).status_code) : null;
  const isTaskComplete = taskStatus === 'TK_Complete';

  const canSave = useMemo(() => {
    if (!task || !taskId) return false;
    if (!selectedTaskRsrcRow || selectedTaskRsrcId == null) return false;
    if (!progressDate) return false;
    if (isTaskComplete) return false;

    const hasAnyQty = providerRows.some(r => (toNum(r.reported_qty) ?? 0) > 0);
    return hasAnyQty;
  }, [task, taskId, selectedTaskRsrcRow, selectedTaskRsrcId, progressDate, providerRows, isTaskComplete]);

  const [machineModalOpen, setMachineModalOpen] = useState(false);
  const [personnelModalOpen, setPersonnelModalOpen] = useState(false);
  const [companyModalOpen, setCompanyModalOpen] = useState(false);

  const handleSelectMachine = useCallback(() => {
    setMachineModalOpen(true);
  }, []);

  const handleSelectPersonnel = useCallback(() => {
    setPersonnelModalOpen(true);
  }, []);

  const handleSelectCompany = useCallback(() => {
    setCompanyModalOpen(true);
  }, []);

  const handleAddProviderRow = useCallback(() => {
    if (isEquip) {
      handleSelectMachine();
      return;
    }
    if (isLabor) {
      handleSelectPersonnel();
      return;
    }
    if (isMat) {
      handleSelectCompany();
      return;
    }
    setProviderRows(prev => {
      const nextIdx = prev.length + 1;
      return [
        ...prev,
        {
          key: `row-${Date.now()}-${nextIdx}`,
          provider_code: '',
          provider_name: '',
          reported_qty: null,
          remarks: '',
        },
      ];
    });
  }, [handleSelectCompany, handleSelectMachine, handleSelectPersonnel, isEquip, isLabor, isMat]);

  const handleRemoveProviderRow = useCallback((key: string) => {
    setProviderRows(prev => {
      if (prev.length <= 1) {
        return isEquip ? [] : prev;
      }
      return prev.filter(r => r.key !== key);
    });
  }, [isEquip]);

  const [saving, setSaving] = useState(false);

  const columns: ColumnsType<ProviderRow> = useMemo(() => {
    return [
      {
        title: 'Provider Code',
        dataIndex: 'provider_code',
        key: 'provider_code',
        width: 120,
        render: (_: any, record: ProviderRow) => (
          (isLabor || isEquip || isMat) ? (
            <Text>{record.provider_code}</Text>
          ) : (
            <Input
              value={record.provider_code}
              onChange={(e) => {
                const v = e.target.value;
                setProviderRows(prev => prev.map(r => (r.key === record.key ? { ...r, provider_code: v } : r)));
              }}
            />
          )
        ),
      },
      {
        title: 'Provider Name',
        dataIndex: 'provider_name',
        key: 'provider_name',
        width: 400,
        render: (_: any, record: ProviderRow) => (
          (isLabor || isEquip || isMat) ? (
            <Text>{record.provider_name}</Text>
          ) : (
            <Input
              value={record.provider_name}
              onChange={(e) => {
                const v = e.target.value;
                setProviderRows(prev => prev.map(r => (r.key === record.key ? { ...r, provider_name: v } : r)));
              }}
            />
          )
        ),
      },
      {
        title: 'Qty',
        dataIndex: 'reported_qty',
        key: 'reported_qty',
        width: 90,
        align: 'right',
        render: (_: any, record: ProviderRow) => (
          <Input
            value={record.reported_qty == null ? '' : String(record.reported_qty)}
            inputMode="decimal"
            style={{ width: '100%', textAlign: 'right' }}
            onChange={(e) => {
              const raw = e.target.value;
              const n = toNum(raw);
              setProviderRows(prev => prev.map(r => (r.key === record.key ? { ...r, reported_qty: n } : r)));
            }}
          />
        ),
      },
      {
        title: 'Remark',
        dataIndex: 'remarks',
        key: 'remarks',
        render: (_: any, record: ProviderRow) => (
          <Input
            value={record.remarks}
            onChange={(e) => {
              const v = e.target.value;
              setProviderRows(prev => prev.map(r => (r.key === record.key ? { ...r, remarks: v } : r)));
            }}
          />
        ),
      },
      {
        title: '',
        key: 'actions',
        width: 60,
        render: (_: any, record: ProviderRow) => (
          <Space size={6}>
            <Button
              danger
              onClick={() => handleRemoveProviderRow(record.key)}
              disabled={saving || (!isEquip && providerRows.length <= 1)}
            >
              X
            </Button>
          </Space>
        ),
      },
    ];
  }, [handleRemoveProviderRow, isEquip, isLabor, isMat, laborProviderOptions, providerRows.length, saving]);

  const handleSave = useCallback(async () => {
    if (!task || !taskId) {
      message.error('Select a valid work permit / task');
      return;
    }
    if (isTaskComplete) {
      message.error('Activity is TK_Complete; updates are not allowed');
      return;
    }
    if (!selectedTaskRsrcRow || selectedTaskRsrcId == null) {
      message.error('Select a resource');
      return;
    }

    const updateDate = progressDate ?? dayjs();
    const progressIso = updateDate.toDate().toISOString();

    const rsrcShortName = String((selectedTaskRsrcRow as any)?.rsrc_short_name ?? '').trim();
    const unitName = (selectedTaskRsrcRow as any)?.unit_name != null ? String((selectedTaskRsrcRow as any).unit_name) : null;
    const effectiveUnitName = (isLabor || isEquip) ? 'hours' : unitName;

    const rowsToInsert = providerRows
      .map((r) => ({
        provider_code: String(r.provider_code ?? '').trim() || null,
        provider_name: String(r.provider_name ?? '').trim() || null,
        reported_qty: toNum(r.reported_qty) ?? 0,
        remarks: String(r.remarks ?? '').trim() || null,
      }))
      .filter((r) => (toNum(r.reported_qty) ?? 0) > 0);

    if (!rowsToInsert.length) {
      message.error('Enter at least one provider qty > 0');
      return;
    }

    if (updateDate.isSame(dayjs(), 'day')) {
      const ok = await new Promise<boolean>((resolve) => {
        Modal.confirm({
          title: 'Confirm date',
          content: 'The selected date is today. Do you want to proceed with today\'s date? If not, click Cancel and change the date.',
          okText: 'Proceed',
          cancelText: 'Cancel',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!ok) return;
    }

    setSaving(true);
    try {
      // 1) Insert provider rows (append-only) into Postgres
      const insertPayload = rowsToInsert.map((r) => ({
        update_date: updateDate.format('YYYY-MM-DD'),
        wp_ref_num: wpRefNum || null,
        activity_code: String(task.task_code ?? taskCode ?? '').trim(),
        activity_name: String(task.task_name ?? taskNameFromWp ?? '').trim(),
        rsrc_unit_name: effectiveUnitName,
        rsrc_short_name: rsrcShortName || null,
        provider_code: r.provider_code,
        provider_name: r.provider_name,
        reported_qty: r.reported_qty,
        remarks: r.remarks,
        created_at: progressIso,
      }));

      await insertData('to_pln_p6_progress_updates', insertPayload);

      // 2) Read cumulative qty from Postgres view
      const sql = `
        SELECT cum_reported_qty
        FROM public.vw_to_pln_p6_progress_updates_cum
        WHERE activity_code = '${String(task.task_code ?? taskCode).replace(/'/g, "''")}'
          AND rsrc_short_name = '${String(rsrcShortName).replace(/'/g, "''")}'
        LIMIT 1;
      `;
      const cumResp = await fetchSqlDataWithQuery(sql);
      const cumRows = unwrapRows<{ cum_reported_qty: any }>(cumResp);
      const cumQty = cumRows.length ? toNum((cumRows[0] as any).cum_reported_qty) ?? 0 : 0;

      // 3) Overwrite P6 TASKRSRC act_reg_qty/remain_qty using existing save helper
      const targetQty = toNum((selectedTaskRsrcRow as any)?.target_qty) ?? 0;
      const nextRemain = clampNonNegative(targetQty - cumQty);

      const nextStatusCode = taskStatus === 'TK_NotStart' ? 'TK_Active' : null;

      const taskDetails: any = {
        task_id: Number(taskId),
        status_code: taskStatus,
        act_start_date: (task as any)?.act_start_date != null ? String((task as any).act_start_date) : null,
        act_end_date: (task as any)?.act_end_date != null ? String((task as any).act_end_date) : null,
        target_start_date: (task as any)?.target_start_date != null ? String((task as any).target_start_date) : null,
        complete_pct_type: (task as any)?.complete_pct_type != null ? String((task as any).complete_pct_type) : null,
        phys_complete_pct: toNum((task as any)?.phys_complete_pct),
        target_drtn_hr_cnt: toNum((task as any)?.target_drtn_hr_cnt),
        remain_drtn_hr_cnt: toNum((task as any)?.remain_drtn_hr_cnt),
      };

      const actRegQtyOld = toNum((selectedTaskRsrcRow as any)?.act_reg_qty);
      const remainQtyOld = toNum((selectedTaskRsrcRow as any)?.remain_qty);

      const p6Rows = (taskResources || [])
        .map((r: any) => {
          const id = r?.taskrsrc_id != null ? Number(r.taskrsrc_id) : NaN;
          if (!Number.isFinite(id)) return null;

          const target = toNum(r?.target_qty);
          const oldAct = toNum(r?.act_reg_qty);
          const oldRemain = toNum(r?.remain_qty);

          const isSelected = String(id) === String(selectedTaskRsrcId);

          return {
            taskrsrc_id: id,
            act_start_date: r?.act_start_date != null ? String(r.act_start_date) : null,
            act_end_date: r?.act_end_date != null ? String(r.act_end_date) : null,
            rsrc_type: r?.rsrc_type != null ? String(r.rsrc_type) : null,
            target_qty: target,
            act_reg_qty: oldAct,
            remain_qty: oldRemain,
            cost_per_qty: toNum(r?.cost_per_qty),
            _new_act_reg_qty: isSelected ? cumQty : oldAct,
            _new_remain_qty: isSelected ? nextRemain : oldRemain,
          };
        })
        .filter(Boolean) as any[];

      const result = await saveP6ProgressUpdate({
        taskId: Number(taskId),
        rows: p6Rows,
        taskDetails,
        nextStatusCode,
        progressIso,
        user,
        database: 'PPMTRAIN',
      });

      if (result.failCount === 0) {
        message.success(`Saved. P6 cumulative qty is now ${cumQty}`);
      } else {
        message.warning(`Saved with warnings: ${result.okCount} updated, ${result.failCount} failed`);
      }
    } catch (e: any) {
      message.error(e?.message || 'Failed to save resource update');
    } finally {
      setSaving(false);
    }
  }, [
    isEquip,
    isLabor,
    isTaskComplete,
    progressDate,
    providerRows,
    selectedTaskRsrcId,
    selectedTaskRsrcRow,
    task,
    taskCode,
    taskId,
    taskNameFromWp,
    taskStatus,
    taskResources,
    user,
    wpRefNum,
  ]);

  return (
    <Card>
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Title level={3} style={{ margin: 0 }}>Actuals Report</Title>
          <Space>
            <DatePicker
              value={progressDate}
              onChange={(d) => setProgressDate(d ?? dayjs())}
              disabled={saving}
              allowClear={false}
              disabledDate={(d) => {
                if (!d) return false;
                return d.isAfter(dayjs(), 'day');
              }}
            />
            <Button onClick={() => setWpModalOpen(true)} disabled={saving}>
              Select Work Permit
            </Button>
            <Button action="edit" type="primary" onClick={handleSave} disabled={!canSave || saving} loading={saving}>
              Save
            </Button>
          </Space>
        </div>

        <WPermitSelect
          open={wpModalOpen}
          onCancel={() => setWpModalOpen(false)}
          onOk={(selected) => {
            const [refNum, desc, actCode, actName] = selected;
            setWpRefNum(refNum);
            setWpDesc(desc);
            setTaskCode(actCode);
            setTaskNameFromWp(actName);
            setWpModalOpen(false);
          }}
        />

        <MachineSelect
          open={machineModalOpen}
          onCancel={() => setMachineModalOpen(false)}
          onOk={({ machine_id, label }) => {
            setProviderRows(prev => {
              const exists = (prev || []).some(r => String(r.provider_code) === String(machine_id));
              if (exists) return prev;

              const emptyIdx = (prev || []).findIndex((r) => {
                const codeEmpty = String(r.provider_code ?? '').trim() === '';
                const nameEmpty = String(r.provider_name ?? '').trim() === '';
                const qtyEmpty = r.reported_qty == null;
                const remarksEmpty = String(r.remarks ?? '').trim() === '';
                return codeEmpty && nameEmpty && qtyEmpty && remarksEmpty;
              });

              const nextRow: ProviderRow = {
                key: emptyIdx >= 0 ? prev[emptyIdx].key : `mach-${machine_id}-${Date.now()}`,
                provider_code: String(machine_id),
                provider_name: String(label),
                reported_qty: null,
                remarks: '',
              };

              if (emptyIdx >= 0) {
                const copy = [...prev];
                copy[emptyIdx] = nextRow;
                return copy;
              }

              return [...prev, nextRow];
            });
            setMachineModalOpen(false);
          }}
          title="Select Machine"
        />

        <PersonnelSelect
          open={personnelModalOpen}
          onCancel={() => setPersonnelModalOpen(false)}
          multi
          mode="code_only"
          onOk={(value) => {
            const selectedIds = Array.isArray(value) ? value.map(v => String(v)) : [];
            if (!selectedIds.length) {
              setPersonnelModalOpen(false);
              return;
            }

            setProviderRows(prev => {
              const existing = new Set((prev || []).map(r => String(r.provider_code)));
              const toAdd: ProviderRow[] = [];

              selectedIds.forEach((pid) => {
                const code = String(pid);
                if (!code || existing.has(code)) return;
                const rec = (personnelRows || []).find((p: any) => String(p?.pers_id) === code) as any;
                const fullName = rec ? `${rec.first_name || ''} ${rec.last_name || ''}`.trim() : '';
                toAdd.push({
                  key: `pers-${code}-${Date.now()}`,
                  provider_code: code,
                  provider_name: fullName || code,
                  reported_qty: null,
                  remarks: '',
                });
                existing.add(code);
              });

              return toAdd.length ? [...prev, ...toAdd] : prev;
            });

            setPersonnelModalOpen(false);
          }}
        />

        <CompaniesSelect
          open={companyModalOpen}
          returnMode="object"
          onCancel={() => setCompanyModalOpen(false)}
          onOk={(value) => {
            const legacyLabel = typeof value === 'string' ? String(value).trim() : '';
            const providerName = typeof value === 'object' && value != null ? String((value as any).name ?? '').trim() : '';
            const providerCode = typeof value === 'object' && value != null ? String((value as any).short_code ?? '').trim() : '';

            const label = typeof value === 'object' && value != null ? String((value as any).label ?? '').trim() : legacyLabel;

            if (!label && !providerName && !providerCode) {
              setCompanyModalOpen(false);
              return;
            }

            // Preferred: structured return value { name, short_code, label }
            // Backward compatible: legacy string "Name (SHORT_CODE)"
            let finalName = providerName;
            let finalCode = providerCode;
            if (!finalName || !finalCode) {
              const m = label.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
              finalName = finalName || (m?.[1] ?? label).trim();
              finalCode = finalCode || (m?.[2] ?? '').trim() || finalName;
            }

            setProviderRows((prev) => {
              const exists = (prev || []).some((r) => String(r.provider_code) === String(finalCode));
              if (exists) return prev;

              const emptyIdx = (prev || []).findIndex((r) => {
                const codeEmpty = String(r.provider_code ?? '').trim() === '';
                const nameEmpty = String(r.provider_name ?? '').trim() === '';
                const qtyEmpty = r.reported_qty == null;
                const remarksEmpty = String(r.remarks ?? '').trim() === '';
                return codeEmpty && nameEmpty && qtyEmpty && remarksEmpty;
              });

              const nextRow: ProviderRow = {
                key: emptyIdx >= 0 ? prev[emptyIdx].key : `comp-${finalCode}-${Date.now()}`,
                provider_code: finalCode,
                provider_name: finalName,
                reported_qty: null,
                remarks: '',
              };

              if (emptyIdx >= 0) {
                const copy = [...prev];
                copy[emptyIdx] = nextRow;
                return copy;
              }
              return [...prev, nextRow];
            });

            setCompanyModalOpen(false);
          }}
        />

        <Card size="small" bodyStyle={{ paddingTop: 10, paddingBottom: 10 }}>
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            <Space size={18} wrap style={{ width: '100%' }}>
              <div>
                <Text type="secondary">WP:</Text>
                <Text strong>{' '}{wpRefNum || '-'}</Text>
                {wpDesc ? <Text type="secondary">{' '}- {wpDesc}</Text> : null}
              </div>

              {(isLoadingTask || isLoadingTaskRsrc) && <Spin size="small" />}
            </Space>

            <Space size={18} wrap style={{ width: '100%' }}>
              <div>
                <Text type="secondary">Status:</Text>
                <Text strong>{' '}{taskStatus ?? '-'}</Text>
                {isTaskComplete ? <Text type="danger">{' '}Updates not allowed</Text> : null}
              </div>

              <div>
                <Text type="secondary">Activity:</Text>
                <Text strong>{' '}{taskCode || '-'}</Text>
                <Text type="secondary">{' '}- {task?.task_name ?? taskNameFromWp ?? '-'}</Text>
              </div>
            </Space>
          </Space>
        </Card>

        <Card size="small" title="Resource" bodyStyle={{ paddingTop: 10, paddingBottom: 10 }}>
          <Space wrap style={{ width: '100%' }} size={14}>
            <Select
              placeholder="Select a resource assignment"
              value={selectedTaskRsrcId ?? undefined}
              onChange={(v) => setSelectedTaskRsrcId(v != null ? Number(v) : null)}
              options={resourceOptions}
              style={{ width: 520 }}
              allowClear
              disabled={!taskId || saving || isTaskComplete}
              showSearch
              filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            />

            {selectedTaskRsrcRow && (
              <Text type="secondary">
                Type: {String((selectedTaskRsrcRow as any)?.rsrc_type ?? '-')}
                {' | '}
                Target: {toNum((selectedTaskRsrcRow as any)?.target_qty) ?? 0}
                {' | '}Actual: {toNum((selectedTaskRsrcRow as any)?.act_reg_qty) ?? 0}
                {' | '}Remain: {toNum((selectedTaskRsrcRow as any)?.remain_qty) ?? 0}
              </Text>
            )}
          </Space>
        </Card>

        <Card
          size="small"
          title={(
            <Space size={8}>
              <span>Provider Entries (batch)</span>
              {!isEquip && (
                <Button
                  type="text"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={handleAddProviderRow}
                  disabled={saving || isTaskComplete || !selectedTaskRsrcId}
                />
              )}
            </Space>
          )}
        >
          <Space direction="vertical" style={{ width: '100%' }} size={8}>
            {isEquip ? (
              <Button onClick={handleSelectMachine} disabled={saving || isTaskComplete || !selectedTaskRsrcId}>
                Select Machine
              </Button>
            ) : null}

            {isMat ? (
              <Button onClick={handleSelectCompany} disabled={saving || isTaskComplete || !selectedTaskRsrcId}>
                Select Company
              </Button>
            ) : null}

            {isLabor && laborProviderOptions.length === 0 && (
              <Text type="secondary">
                Labor resource selected: providers are sourced from the selected work permit personnel list.
              </Text>
            )}

            {isEquip && (
              <Text type="secondary">
                Equipment resource selected: provider selection will use a machine selection modal.
              </Text>
            )}

            {isMat && (
              <Text type="secondary">
                Measurable (RT_Mat) resource selected: provider will be selected from the companies list.
              </Text>
            )}

            <Table<ProviderRow>
              size="small"
              rowKey={(r) => r.key}
              dataSource={providerRows}
              columns={columns}
              pagination={false}
            />
            <Text type="secondary">
              Saving will append provider lines to Postgres and overwrite P6 actual qty to the all-time cumulative total for this activity + resource.
            </Text>
          </Space>
        </Card>

      </Space>
    </Card>
  );
};

export default ToPlnActualsReport;
